// /objects — Object Capture: turn a walk-around video (and/or 8+ photos) of a single object
// into a background-removed, explorable gaussian-splat digital twin. Upload card (drag-drop,
// explicit submission status panel: uploading % → confirming (server HeadObject-verifies the
// media) → visible green success before navigating; loud red failure with a Try-again that
// reuses the same objectId/presigns and transparently re-creates on stale ones) + a gallery
// grid polled while jobs run.
import { useEffect, useRef, useState } from "react";
import type { ChangeEvent, DragEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  createObject,
  listObjects,
  putFileWithProgress,
  startObject,
  type ObjectListItem,
  type ObjectMediaInput,
  type ObjectUploadTarget,
} from "../lib/apiClient";
import { CAPTURE_MODES, type CaptureMode } from "../lib/captureModes";
import { objectStageCopy } from "../lib/objectStages";
import { formatDateTime } from "../lib/util";
import { EmptyState, SectionTitle, Spinner } from "./ui";

const ACCEPT = "video/mp4,video/quicktime,image/jpeg,image/png,.mp4,.mov,.jpg,.jpeg,.png";
const DARK_BACKDROP = "radial-gradient(ellipse at 50% 35%, #2a2c33 0%, #0c0c10 75%)";

/** Resolve one of the four allowed content types. Extension fallback because e.g. .mov files
 *  sometimes arrive with an empty File.type. null = not uploadable. */
function allowedContentType(f: File): string | null {
  const t = (f.type || "").toLowerCase();
  if (t === "video/mp4" || t === "video/quicktime" || t === "image/jpeg" || t === "image/png") return t;
  const ext = f.name.toLowerCase().split(".").pop() ?? "";
  if (ext === "mp4") return "video/mp4";
  if (ext === "mov") return "video/quicktime";
  if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
  if (ext === "png") return "image/png";
  return null;
}

const isVideoType = (ct: string) => ct.startsWith("video/");

function fmtBytes(n: number): string {
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)} GB`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1e3))} KB`;
}

/** Constraint check (≤2 videos; ≥8 images when no video). Returns a friendly error or null. */
function validationError(files: File[]): string | null {
  if (files.length === 0) return null;
  for (const f of files) {
    if (!allowedContentType(f)) {
      return `"${f.name}" isn't a supported type — use MP4/MOV video or JPEG/PNG photos.`;
    }
  }
  const videos = files.filter((f) => isVideoType(allowedContentType(f)!)).length;
  const images = files.length - videos;
  if (videos > 2) return "At most 2 videos per object — pick your best walk-around loop.";
  if (videos === 0 && images < 8) {
    return `Photo-only captures need at least 8 photos (you have ${images}) — or add a walk-around video.`;
  }
  return null;
}

const clampPct = (p: number) => Math.max(0, Math.min(100, p));

// ---------------------------------------------------------------------------
// Submission state machine

/** idle → uploading (PUTs with progress) → confirming (/start HeadObject-verifies) →
 *  success (shown ~1.5 s, then navigate). Any failure → error (loud panel + Try again). */
type SubmitPhase = "idle" | "uploading" | "confirming" | "success" | "error";

/** One create-object attempt. Kept across a failure so Try Again can re-use the SAME
 *  objectId + presigned URLs (skipping files whose PUT already succeeded) instead of
 *  minting an orphan row per retry. */
interface UploadAttempt {
  objectId: string;
  uploads: ObjectUploadTarget[]; // uploads[i] matches files[i]
  files: File[];
  label?: string;
  captureMode: CaptureMode;
  /** Files whose PUT completed 2xx — skipped on a same-attempt retry. */
  uploaded: boolean[];
}

/** Failures that mean the attempt's server side is unusable: presigned PUT expired
 *  (S3 answers 403) or /start's HeadObject verification found media missing. Try Again
 *  responds by transparently creating a fresh object with the same files. */
function attemptIsStale(e: unknown): boolean {
  return e instanceof ApiError && (e.code === "uploads_incomplete" || e.status === 403);
}

export function ObjectsView() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [files, setFiles] = useState<File[]>([]);
  const [label, setLabel] = useState("");
  const [captureMode, setCaptureMode] = useState<CaptureMode>("object");
  const [dragOver, setDragOver] = useState(false);
  const [phase, setPhase] = useState<SubmitPhase>("idle");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [uploadIdx, setUploadIdx] = useState(0);
  const [fileFracs, setFileFracs] = useState<number[]>([]);

  const attemptRef = useRef<UploadAttempt | null>(null);
  const navTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (navTimerRef.current !== null) window.clearTimeout(navTimerRef.current);
    },
    [],
  );

  // Submission in flight (dropzone + file edits locked). "error" is deliberately NOT busy:
  // the files stay editable, and any edit resets the failed attempt below.
  const busy = phase === "uploading" || phase === "confirming" || phase === "success";
  const validation = validationError(files);
  const defaultLabel = files[0] ? files[0].name.replace(/\.[^.]+$/, "") : "";

  const totalBytes = files.reduce((a, f) => a + f.size, 0) || 1;
  const overallFrac =
    files.reduce((a, f, i) => a + (fileFracs[i] ?? 0) * f.size, 0) / totalBytes;

  // Leaving mid-upload kills the PUTs — warn (browsers show their own generic prompt).
  const uploadInFlight = phase === "uploading" || phase === "confirming";
  useEffect(() => {
    if (!uploadInFlight) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ""; // Chrome requires returnValue to show the prompt
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [uploadInFlight]);

  const objectsQuery = useQuery({
    queryKey: ["objects"],
    queryFn: listObjects,
    // Poll only while a job is actively queued/processing. Terminal rows and abandoned
    // awaiting-upload rows must not keep the list polling forever.
    refetchInterval: (query) => {
      const objs = query.state.data as ObjectListItem[] | undefined;
      return objs && objs.some((o) => o.status === "queued" || o.status === "processing")
        ? 8000
        : false;
    },
  });

  /** A failed attempt is pinned to the exact file set it presigned — editing files makes
   *  it meaningless, so any file mutation clears the error state back to a clean idle. */
  const resetFailedAttempt = () => {
    if (phase !== "error") return;
    attemptRef.current = null;
    setPhase("idle");
    setSubmitError(null);
    setFileFracs([]);
  };

  const addFiles = (list: FileList | null) => {
    if (!list || list.length === 0 || busy) return;
    resetFailedAttempt();
    setFiles((prev) => {
      const next = [...prev];
      for (const f of Array.from(list)) {
        // Dedupe on name+size so re-drops don't double up.
        if (!next.some((p) => p.name === f.name && p.size === f.size)) next.push(f);
      }
      return next;
    });
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    addFiles(e.target.files);
    e.target.value = ""; // allow re-picking the same file
  };

  /** POST /v1/objects for the current file set → a fresh attempt (nothing uploaded yet). */
  const createAttempt = async (): Promise<UploadAttempt> => {
    const snapshot = [...files];
    const media: ObjectMediaInput[] = snapshot.map((f) => {
      const ct = allowedContentType(f)!;
      return { kind: isVideoType(ct) ? "video" : "image", contentType: ct, fileName: f.name };
    });
    // Server rejects labels > 120 chars — truncate (long filenames otherwise fail the create).
    const rawLabel = label.trim() || defaultLabel;
    const finalLabel = rawLabel ? rawLabel.slice(0, 120) : undefined;
    const { objectId, uploads } = await createObject(media, finalLabel, captureMode);
    return {
      objectId,
      uploads,
      files: snapshot,
      label: finalLabel,
      captureMode,
      uploaded: snapshot.map(() => false),
    };
  };

  /** Sequential PUTs (uploads[i] matches files[i], server-echoed contentType), skipping
   *  files that already landed, then /start — which HeadObject-verifies every upload on
   *  the server before queueing (that's what "Confirming upload…" is). */
  const runAttempt = async (attempt: UploadAttempt) => {
    setPhase("uploading");
    // Per-file fractions drive both the per-file bars and the bytes-weighted overall bar.
    const fracs: number[] = attempt.files.map((_, i) => (attempt.uploaded[i] ? 1 : 0));
    setFileFracs([...fracs]);
    for (let i = 0; i < attempt.files.length; i++) {
      if (attempt.uploaded[i]) continue;
      setUploadIdx(i);
      await putFileWithProgress(
        attempt.uploads[i].url,
        attempt.files[i],
        attempt.uploads[i].contentType,
        (frac) => {
          fracs[i] = frac;
          setFileFracs([...fracs]);
        },
      );
      fracs[i] = 1;
      attempt.uploaded[i] = true;
      setFileFracs([...fracs]);
    }
    setPhase("confirming");
    await startObject(attempt.objectId);
  };

  /** Show the green confirmation for a beat BEFORE navigating — the user must see that
   *  the upload landed; the detail page then re-confirms with its own uploaded chip. */
  const finishSuccess = (objectId: string) => {
    setPhase("success");
    setSubmitError(null);
    queryClient.invalidateQueries({ queryKey: ["objects"] });
    navTimerRef.current = window.setTimeout(() => navigate(`/objects/${objectId}`), 1500);
  };

  const failWith = (e: unknown) => {
    setSubmitError(e instanceof Error ? e.message : String(e));
    setPhase("error");
  };

  const handleCreate = async () => {
    if (busy || files.length === 0 || validation) return;
    setSubmitError(null);
    setUploadIdx(0);
    setFileFracs(files.map(() => 0));
    try {
      setPhase("uploading"); // covers the (fast) presign round-trip too
      const attempt = await createAttempt();
      attemptRef.current = attempt;
      await runAttempt(attempt);
      finishSuccess(attempt.objectId);
    } catch (e) {
      failWith(e);
    }
  };

  /** Try again: re-use the same objectId + presigns (already-landed files are skipped);
   *  if those are stale (expired presign / uploads_incomplete), transparently create a
   *  fresh object with the same files and retry once. */
  const handleRetry = async () => {
    if (busy || files.length === 0) return;
    setSubmitError(null);
    const prior = attemptRef.current;
    try {
      if (prior) {
        try {
          await runAttempt(prior);
          finishSuccess(prior.objectId);
          return;
        } catch (e) {
          if (!attemptIsStale(e)) throw e;
          // Presigns expired or the server saw missing media — fall through to a
          // fresh object with the same files (the old row stays inert awaiting-upload).
        }
      }
      setUploadIdx(0);
      setFileFracs(files.map(() => 0));
      setPhase("uploading");
      const fresh = await createAttempt();
      attemptRef.current = fresh;
      await runAttempt(fresh);
      finishSuccess(fresh.objectId);
    } catch (e) {
      failWith(e);
    }
  };

  const objects = objectsQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-ink">3D Objects</h1>
        <p className="text-sm text-muted">
          Walk around any object with your camera — get an explorable 3D digital twin with the
          background removed.
        </p>
      </div>

      {/* Upload card */}
      <div className="card space-y-4 p-5">
        {/* Capture-mode picker lives ABOVE the dropzone: the tip changes what the user
            films, so it must be visible before any files exist. */}
        <div className="flex flex-wrap items-center gap-3">
          <div className={`flex rounded-full bg-cream p-1 text-sm ${busy ? "pointer-events-none opacity-60" : ""}`}>
            {CAPTURE_MODES.map((m) => (
              <button
                key={m.id}
                className={`rounded-full px-3 py-1 transition ${
                  captureMode === m.id ? "bg-white font-semibold text-ink shadow-sm" : "text-muted"
                }`}
                disabled={busy}
                onClick={() => {
                  if (m.id === captureMode) return;
                  // A failed attempt is pinned to its mode the same way it is pinned to
                  // its file set — switching modes discards it.
                  resetFailedAttempt();
                  setCaptureMode(m.id);
                }}
              >
                {m.label}
              </button>
            ))}
          </div>
          <p className="min-w-0 flex-1 text-xs text-muted">
            {CAPTURE_MODES.find((m) => m.id === captureMode)?.tip}
          </p>
        </div>
        <div
          className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-card border-2 border-dashed px-6 py-10 text-center transition ${
            dragOver ? "border-accent bg-cream/80" : "border-black/10 bg-cream/40 hover:bg-cream/60"
          } ${busy ? "pointer-events-none opacity-60" : ""}`}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <div className="text-3xl opacity-70">⬢</div>
          <p className="font-semibold text-ink">Drop a walk-around video or photos here</p>
          <p className="max-w-md text-sm text-muted">
            One slow loop around the object works best. MP4/MOV video (up to 2), or 8+ JPEG/PNG
            photos. Click to browse.
          </p>
          <p className="max-w-md text-xs text-muted">
            Best results: one continuous walk-around video, or a photo set — mixing both may drop
            the lower-resolution media.
          </p>
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={onPick}
        />

        {files.length > 0 && (
          <div className="space-y-1.5">
            {files.map((f, i) => {
              const ct = allowedContentType(f);
              return (
                <div
                  key={`${f.name}-${f.size}`}
                  className="flex items-center justify-between gap-3 rounded-card bg-cream/50 px-3 py-2 text-sm"
                >
                  <span className="min-w-0 flex-1 truncate text-ink">
                    <span className="mr-2">{ct && isVideoType(ct) ? "🎬" : "🖼️"}</span>
                    {f.name}
                  </span>
                  <span className="shrink-0 text-xs text-muted">{fmtBytes(f.size)}</span>
                  {!busy && (
                    <button
                      className="shrink-0 rounded-full px-2 text-muted transition hover:text-red-500"
                      onClick={() => {
                        resetFailedAttempt();
                        setFiles((prev) => prev.filter((_, j) => j !== i));
                      }}
                      title="Remove"
                    >
                      ✕
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {files.length > 0 && phase === "idle" && (
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={defaultLabel || "Label (optional)"}
              className="min-w-0 flex-1 rounded-full border border-black/10 bg-white px-4 py-2 text-sm text-ink placeholder:text-muted/70"
            />
            <button className="btn-warm text-sm" onClick={handleCreate} disabled={!!validation}>
              Create 3D model
            </button>
          </div>
        )}

        {validation && <p className="text-sm text-red-500">{validation}</p>}

        {/* Submission status panel — always visible from the moment Create is clicked
            until the navigate (or until a failed attempt is edited away). */}
        {phase === "uploading" || phase === "confirming" ? (
          <div className="space-y-2 rounded-card border border-black/10 bg-cream/40 p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="font-semibold text-ink">
                {phase === "confirming"
                  ? "Confirming upload…"
                  : `Uploading — ${Math.round(clampPct(overallFrac * 100))}%`}
              </p>
              {phase === "uploading" && files.length > 1 && (
                <span className="shrink-0 text-xs text-muted">
                  file {Math.min(uploadIdx + 1, files.length)} of {files.length}
                </span>
              )}
              {phase === "confirming" && (
                <div className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-accent border-t-transparent" />
              )}
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-cream">
              <div
                className="h-full rounded-full bg-warm transition-all"
                style={{ width: `${phase === "confirming" ? 100 : Math.round(clampPct(overallFrac * 100))}%` }}
              />
            </div>
            {phase === "uploading" && files.length > 1 && (
              <div className="space-y-1 pt-1">
                {files.map((f, i) => {
                  const frac = fileFracs[i] ?? 0;
                  return (
                    <div key={`${f.name}-${f.size}`} className="flex items-center gap-2 text-xs">
                      <span className="min-w-0 flex-1 truncate text-muted">{f.name}</span>
                      <div className="h-1.5 w-24 shrink-0 overflow-hidden rounded-full bg-cream">
                        <div
                          className="h-full rounded-full bg-warm transition-all"
                          style={{ width: `${Math.round(clampPct(frac * 100))}%` }}
                        />
                      </div>
                      <span className="w-8 shrink-0 text-right text-muted">
                        {frac >= 1 ? "✓" : `${Math.round(clampPct(frac * 100))}%`}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            <p className="text-xs text-muted">
              {phase === "confirming"
                ? "The server is verifying every file landed before starting the job."
                : "Keep this tab open — leaving now cancels the upload."}
            </p>
          </div>
        ) : phase === "success" ? (
          <div className="space-y-1 rounded-card border border-green-200 bg-green-50 p-4">
            <p className="font-semibold text-green-700">✓ Upload complete — processing started</p>
            <p className="text-xs text-green-700/80">Taking you to your new object…</p>
          </div>
        ) : phase === "error" ? (
          <div className="space-y-2 rounded-card border border-red-200 bg-red-50 p-4">
            <p className="font-semibold text-red-600">
              Upload failed — the model was NOT submitted.
            </p>
            {submitError && <p className="text-sm text-red-600/90">{submitError}</p>}
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <button className="btn-warm text-sm" onClick={handleRetry}>
                Try again
              </button>
              <span className="text-xs text-muted">
                Your files are still selected — retrying resumes where it left off.
              </span>
            </div>
          </div>
        ) : null}
      </div>

      {/* Gallery */}
      <section>
        <SectionTitle>Your objects</SectionTitle>
        {objectsQuery.isLoading ? (
          <Spinner />
        ) : objectsQuery.error ? (
          <div className="card p-4 text-sm text-red-500">
            Couldn't load your objects:{" "}
            {objectsQuery.error instanceof Error ? objectsQuery.error.message : "unknown error"}
          </div>
        ) : objects.length === 0 ? (
          <EmptyState
            title="No 3D objects yet"
            subtitle="Upload a walk-around video of a truck, a sofa, a grill — anything — and it becomes an explorable digital twin."
            icon="⬢"
          />
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {objects.map((o) => (
              <Link
                key={o.objectId}
                to={`/objects/${o.objectId}`}
                className="card overflow-hidden transition hover:shadow-lift"
              >
                <div className="relative aspect-square w-full" style={{ background: DARK_BACKDROP }}>
                  {o.status === "completed" && o.posterUrl ? (
                    <img
                      src={o.posterUrl}
                      alt={o.label ?? "3D object"}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
                      {o.status === "failed" ? (
                        <span className="rounded-full bg-red-500/15 px-3 py-1 text-xs font-semibold text-red-400">
                          Failed
                        </span>
                      ) : o.status === "completed" ? (
                        <span className="text-3xl text-white/60">⬢</span>
                      ) : o.status === "awaiting-upload" ? (
                        <>
                          <span className="rounded-full bg-amber/25 px-3 py-1 text-xs font-semibold text-amber-dark">
                            Upload incomplete
                          </span>
                          <p className="text-[11px] leading-snug text-white/50">
                            This one never finished uploading — create it again.
                          </p>
                        </>
                      ) : (
                        <>
                          <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/80">
                            {objectStageCopy(o.stage ?? o.status)}
                          </span>
                          {typeof o.pct === "number" && (
                            <div className="h-1.5 w-2/3 overflow-hidden rounded-full bg-white/15">
                              <div
                                className="h-full rounded-full bg-accent-dark transition-all"
                                style={{ width: `${clampPct(o.pct)}%` }}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1.5">
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">
                      {o.label || o.objectId.replace(/^obj-/, "").slice(0, 8)}
                    </span>
                    {(o.captureMode === "scene" || o.captureMode === "subject") && (
                      <span className="shrink-0 rounded-full bg-cream px-2 py-0.5 text-[10px] font-medium text-muted">
                        {o.captureMode === "scene" ? "Scene" : "Twin"}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted">{formatDateTime(o.createdAt)}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
